import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useOfferFilters } from '@/data/catalog/offer';
import type { CheapestOffer } from '@/models/catalog/offer';
import { formatOfferFiltersParams } from '@/utils/format-offer-filter-params';
import { FilterOptions, OfferContextType, ProductFilters } from './interfaces';

const OfferContext = createContext<OfferContextType | null>(null);

const parseFiltersFromUrl = (
  searchParams: URLSearchParams,
  courseId: string
): ProductFilters => {
  const location = searchParams.get('location');
  const [state = '', city = ''] = location
    ? decodeURIComponent(location.replace(/\+/g, ' '))
        .split(',')
        .map((s) => s.trim())
    : [];

  return {
    courseId,
    state,
    city,
    hub: searchParams.get('hub') ?? '',
    period: searchParams.get('period') ?? '',
  };
};

const filtersFromCheapestOffer = (
  offer: CheapestOffer,
  courseId: string
): ProductFilters => ({
  courseId,
  state: offer.hub_state ?? '',
  city: offer.hub_city ?? '',
  hub: offer.hub_id?.toString() ?? '',
  period: offer.period?.type ?? '',
});

export const OfferProvider = ({ children }: { children: ReactNode }) => {
  const { id: courseId = '' } = useParams();
  const [searchParams] = useSearchParams();

  const hasFiltersInUrl = !!(
    searchParams.get('location') ||
    searchParams.get('hub') ||
    searchParams.get('period')
  );

  const initialFilters = parseFiltersFromUrl(searchParams, courseId);

  // Filters shown in the form (may include data from cheapestOffer)
  const [displayFilters, setDisplayFilters] =
    useState<ProductFilters>(initialFilters);

  // Filters used for API requests (only changes on user interaction)
  const [requestFilters, setRequestFilters] =
    useState<ProductFilters>(initialFilters);

  // Tracks if user has interacted with filters (prevents re-populating after clear)
  const [userHasInteracted, setUserHasInteracted] = useState(hasFiltersInUrl);

  const numericCourseId = Number(courseId);

  const queryParams = useMemo(
    () =>
      formatOfferFiltersParams({
        courseId: numericCourseId || undefined,
        hub: requestFilters.hub ? [requestFilters.hub] : undefined,
        location:
          requestFilters.state || requestFilters.city
            ? [requestFilters.state, requestFilters.city]
            : undefined,
        period: requestFilters.period ? [requestFilters.period] : undefined,
      }),
    [
      numericCourseId,
      requestFilters.hub,
      requestFilters.state,
      requestFilters.city,
      requestFilters.period,
    ]
  );

  const { data, isFetching } = useOfferFilters({
    courseId: numericCourseId,
    enabled: !!numericCourseId,
    queryParams,
  });

  const cheapestOffer = data?.cheapest_offer;
  const responseFilters = data?.filters;

  const options = useMemo<FilterOptions>(() => {
    const states = responseFilters?.states ?? [];
    const cities = responseFilters?.cities.length
      ? responseFilters.cities
      : cheapestOffer?.hub_city
        ? [cheapestOffer.hub_city]
        : [];
    const hubs = responseFilters?.hubs.length
      ? responseFilters.hubs
      : cheapestOffer?.hub_id && cheapestOffer?.hub_name
        ? [{ id: cheapestOffer.hub_id, name: cheapestOffer.hub_name }]
        : [];
    const periods = responseFilters?.periods.length
      ? responseFilters.periods
      : cheapestOffer?.period
        ? [cheapestOffer.period]
        : [];

    return { states, cities, hubs, periods };
  }, [responseFilters, cheapestOffer]);

  // Populate display filters from cheapestOffer only on initial load (not after user clears)
  useEffect(() => {
    const shouldPopulateFilters =
      !userHasInteracted && cheapestOffer?.hub_state && !displayFilters.state;
    if (shouldPopulateFilters) {
      setDisplayFilters(filtersFromCheapestOffer(cheapestOffer, courseId));
    }
  }, [userHasInteracted, cheapestOffer, displayFilters.state, courseId]);

  // Updates both display and request filters (triggers new request)
  const updateFilters = useCallback((partial: Partial<ProductFilters>) => {
    setUserHasInteracted(true);
    setDisplayFilters((previous) => ({ ...previous, ...partial }));
    setRequestFilters((previous) => ({ ...previous, ...partial }));
  }, []);

  const value: OfferContextType = useMemo(
    () => ({
      filters: displayFilters,
      updateFilters,
      options,
      isLoading: isFetching,
      isSingleOffer: data?.offers_quantity === 1,
      cheapestOffer,
      checkoutConfig: cheapestOffer?.checkout_config,
      pricing: cheapestOffer?.pricing_description,
      isFree: cheapestOffer?.pricing_description?.discount_percentage === 100,
      isFullIncluded: cheapestOffer?.is_full_included ?? false,
    }),
    [
      displayFilters,
      updateFilters,
      options,
      data?.offers_quantity,
      isFetching,
      cheapestOffer,
    ]
  );

  return (
    <OfferContext.Provider value={value}>{children}</OfferContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOffer = (): OfferContextType => {
  const context = useContext(OfferContext);
  if (!context)
    throw new Error('useOffer must be used within an OfferProvider');
  return context;
};
